import React, { useCallback, useState, useEffect } from 'react';
import {
  CommandSelect,
  CommandSelectTrigger,
  CommandSelectValue,
  CommandSelectContent,
  CommandSelectItem,
  CommandSelectEmpty,
} from './command-select';

export type DateFilterMethodValue = 'insight_date' | 'post_date_current' | 'post_date_lifetime';

export interface DateFilterMethodSelectProps {
  children: React.ReactNode;
}

export const DateFilterMethodSelect: React.FC<DateFilterMethodSelectProps> = ({ children }) => {
  return (
    <div className="relative">
      {children}
    </div>
  );
};

export const DateFilterMethodOptions = ({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: DateFilterMethodValue; label: string; description: string }>;
  value: DateFilterMethodValue;
  onChange: (value: DateFilterMethodValue) => void;
}) => {
  const [localValue, setLocalValue] = useState<DateFilterMethodValue>(value);
  const [isUpdating, setIsUpdating] = useState(false);

  // Sync local state with prop value
  useEffect(() => {
    if (!isUpdating) {
      if (localValue !== value) {
        setLocalValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isUpdating]); // localValue intentionally excluded to prevent infinite loop

  // Unified update handler with proper state management
  const updateSelection = useCallback(
    (newSelection: DateFilterMethodValue) => {
      setIsUpdating(true);
      setLocalValue(newSelection);
      onChange(newSelection);

      // Reset updating flag after state has settled
      setTimeout(() => {
        setIsUpdating(false);
      }, 0);
    },
    [onChange],
  );

  const handleDismiss = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      // Don't clear the selection for single select, just close
    },
    [],
  );

  // Display text for selected option
  const getDisplayText = () => {
    const selectedOption = options.find((opt) => opt.value === localValue);
    return selectedOption?.label || 'Select date filter method...';
  };

  return (
    <CommandSelect>
      <CommandSelectTrigger placeholder="Select date filter method...">
        <CommandSelectValue
          placeholder="Select date filter method..."
          displayText={localValue ? getDisplayText() : undefined}
          onClear={handleDismiss}
          disabled={isUpdating}
          showClear={false} // Don't show clear button for single select
        />
      </CommandSelectTrigger>
      <CommandSelectContent maxHeight="200px" showSearch={false}>
        <DateFilterMethodContent
          options={options}
          localValue={localValue}
          isUpdating={isUpdating}
          updateSelection={updateSelection}
        />
      </CommandSelectContent>
    </CommandSelect>
  );
};

// Component that renders the options without search
const DateFilterMethodContent = ({
  options,
  localValue,
  isUpdating,
  updateSelection,
}: {
  options: Array<{ value: DateFilterMethodValue; label: string; description: string }>;
  localValue: DateFilterMethodValue;
  isUpdating: boolean;
  updateSelection: (newSelection: DateFilterMethodValue) => void;
}) => {
  const selectOption = useCallback((optionValue: DateFilterMethodValue) => {
    if (isUpdating) return;
    updateSelection(optionValue);
  }, [updateSelection, isUpdating]);

  return (
    <>
      {options.length > 0 ? (
        options.map(({ value: optionValue, label: optionLabel, description }) => {
          const isSelected = localValue === optionValue;

          return (
            <CommandSelectItem
              key={optionValue}
              value={optionValue}
              onSelect={() => selectOption(optionValue)}
              selected={isSelected}
              disabled={isUpdating}
              className="pl-4"
              showCheckbox={false} // Don't show checkbox for single select
            >
              <div className="flex flex-col">
                <span className="font-medium">{optionLabel}</span>
                <span className="text-xs text-muted-foreground">{description}</span>
              </div>
            </CommandSelectItem>
          );
        })
      ) : (
        <CommandSelectEmpty>No date filter methods found.</CommandSelectEmpty>
      )}
    </>
  );
};
